import type { ScalarField } from './grid.ts';
import { Grain, splitLayer, WEAK_LAYER_GRAINS, type SnowColumns } from './snowpack.ts';

/**
 * How the snowpack changes with time: settlement, grain aging, and liquid water (rain and
 * melt). Deliberately simplified — enough to make storm snow settle and bond over a few days and
 * to make warm or rained-on snow wet and weak.
 *
 * Existing (pre-scenario) snow is taken as the user describes it and is not settled further;
 * only layers deposited during the scenario (and weak layers placed just before it) evolve.
 */

export const EVOLUTION_MODEL = {
  /** Layers deposited before this hour count as old, settled snow and don't compact. */
  youngAfterHour: -48,
  /**
   * Compaction after Anderson (1976) as used in SNTHERM: destructive metamorphism plus
   * overburden (viscous) compaction. Fractional thinning rate, s⁻¹.
   */
  destructive: { c3: 2.777e-6, c4: 0.04, densityThreshold: 100, densityDecay: 0.046 },
  overburden: { eta0: 3.6e6, c5: 0.08, c6: 0.021 },
  maxDensity: 550,
  /** New snow (PP) turns into decomposing fragments after this many hours… */
  ppToDfHours: 24,
  /** …and into rounded grains after this many. */
  dfToRgHours: 120,
  /** Degree-day melt factor (kg m⁻² °C⁻¹ day⁻¹, i.e. mm/°C/day). */
  degreeDayFactor: 3,
  /** Degree-day refreeze factor below 0 °C (mm/°C/day), acting on the top `refreezeDepth`. */
  refreezeFactor: 2,
  refreezeDepth: 0.3,
  /** A refrozen layer this dense or more is an ice crust (IF) rather than a melt-freeze crust. */
  iceDensity: 650,
  /** Liquid water a layer can hold, as a fraction of its volume (irreducible saturation). */
  holdingCapacity: 0.05,
  /** Volumetric liquid water content above which a layer counts as wet. */
  wetThreshold: 0.005,
  /**
   * Melt water enters a surface layer this thick: a thicker top layer is split first, so a
   * sunny afternoon wets the top decimetres rather than a trace of water in a metre of snow.
   */
  surfaceLayerDepth: 0.2,
  /** Don't split off (or leave behind) slivers thinner than this. */
  minSplitThickness: 0.05,
  /**
   * Clear-sky melt by exposure (see `solarMeltFactor`): midday solar elevation (deg; ~62°N in
   * March–April) and the share of melt energy from direct sun — the rest (diffuse and longwave
   * radiation, warm air) doesn't depend on slope and aspect.
   */
  solarElevation: 35,
  directSunShare: 0.5,
} as const;

const G = 9.81;
const WATER_DENSITY = 1000;
const DEG = Math.PI / 180;

/** Volumetric liquid water content (0…1) of a layer. */
export function volumetricLwc(cols: SnowColumns, layer: number): number {
  const h = cols.mass[layer]! / cols.density[layer]!;
  return h > 0 ? cols.water[layer]! / WATER_DENSITY / h : 0;
}

/**
 * Snow temperature (°C) at `depthBelowSurface` in a column of total depth `hs`: air temperature
 * (capped at 0) at the surface, 0 °C at the ground, linear in between. Crude, but it gives the
 * right sign to temperature-dependent processes.
 */
export function snowTemperature(airTemp: number, depthBelowSurface: number, hs: number): number {
  const surface = Math.min(0, airTemp);
  if (hs <= 0) return surface;
  return surface * (1 - Math.min(1, depthBelowSurface / hs));
}

/**
 * Clear-sky melt multiplier per cell: direct midday sun on the slope relative to flat ground,
 * blended with the exposure-independent part of the melt. A steep north face melts at about
 * half the flat-ground rate, a steep south face at about 1.3×. Simplified from Hock (1999)'s
 * enhanced temperature-index melt model.
 */
export function solarMeltFactor(slope: ScalarField, aspect: ScalarField): Float32Array {
  const { solarElevation, directSunShare } = EVOLUTION_MODEL;
  const sinH = Math.sin(solarElevation * DEG);
  const cosH = Math.cos(solarElevation * DEG);
  const out = new Float32Array(slope.data.length);
  for (let i = 0; i < out.length; i++) {
    const s = slope.data[i]! * DEG;
    const a = aspect.data[i]!;
    // Sun due south at midday; flat cells (aspect NaN) see it as flat ground does.
    const facing = Number.isNaN(a) ? 0 : Math.cos((a - 180) * DEG);
    const direct = Math.max(0, sinH * Math.cos(s) + cosH * Math.sin(s) * facing) / sinH;
    out[i] = 1 - directSunShare + directSunShare * direct;
  }
  return out;
}

/**
 * Advance every column by `dtHours`: compact young layers, age grains, melt the surface when the
 * air is above freezing. `now` is the simulation hour at the end of the step.
 * Returns the melt water that ran off the bottom of the snowpack, summed over cells (kg/m²;
 * multiply by the cell area for kg).
 */
export function evolveColumns(
  cols: SnowColumns,
  now: number,
  dtHours: number,
  airTemp: Float32Array,
  /** Per-cell melt multiplier (clear-sky exposure, `solarMeltFactor`); 1 where omitted. */
  meltFactor?: Float32Array,
): number {
  const { maxLayers, count, mass, density, grain, depositedAt, water } = cols;
  const dt = dtHours * 3600;
  const { destructive: d, overburden: o } = EVOLUTION_MODEL;
  let runoff = 0;
  for (let i = 0; i < count.length; i++) {
    if (count[i]! === 0) continue;
    const base = i * maxLayers;
    const ta = airTemp[i]!;

    // Surface melt when the air is above freezing: frozen mass → liquid water, working down
    // through the top layers. Melting removes volume, so density stays and mass shrinks.
    if (ta > 0) {
      const topK = count[i]! - 1;
      const surface = EVOLUTION_MODEL.surfaceLayerDepth;
      if (mass[base + topK]! / density[base + topK]! > 2 * surface)
        splitLayer(cols, i, topK, surface);
      let melt = ((meltFactor?.[i] ?? 1) * EVOLUTION_MODEL.degreeDayFactor * ta * dtHours) / 24;
      let meltWater = 0;
      while (melt > 0 && count[i]! > 0) {
        const top = base + count[i]! - 1;
        const take = Math.min(mass[top]!, melt);
        mass[top] = mass[top]! - take;
        melt -= take;
        meltWater += take;
        if (mass[top]! <= 1e-4) {
          // Layer gone: its water joins the melt water.
          meltWater += water[top]!;
          water[top] = 0;
          count[i]!--;
        }
      }
      runoff += addLiquidWater(cols, i, meltWater);
    } else if (ta < 0) {
      refreeze(cols, i, (EVOLUTION_MODEL.refreezeFactor * -ta * dtHours) / 24);
    }
    const n = count[i]!;
    if (n === 0) continue;

    let hs = 0;
    for (let k = 0; k < n; k++) hs += mass[base + k]! / density[base + k]!;

    // Walk from the top so the overburden above each layer is known.
    let above = 0; // kg/m² of snow (+ water) above the current layer
    let depthAbove = 0;
    for (let k = n - 1; k >= 0; k--) {
      const idx = base + k;
      const m = mass[idx]!;
      const rho = density[idx]!;
      const h = m / rho;
      const age = now - depositedAt[idx]!;
      const wet = water[idx]! / WATER_DENSITY / Math.max(h, 1e-9) > EVOLUTION_MODEL.wetThreshold;

      if (depositedAt[idx]! >= EVOLUTION_MODEL.youngAfterHour && rho < EVOLUTION_MODEL.maxDensity) {
        const t = snowTemperature(ta, depthAbove + h / 2, hs);
        const tk = -t; // Tf − T in K
        const c1 =
          rho > d.densityThreshold ? Math.exp(-d.densityDecay * (rho - d.densityThreshold)) : 1;
        const c2 = wet ? 2 : 1;
        const crDestructive = d.c3 * c1 * c2 * Math.exp(-d.c4 * tk);
        const pressure = G * (above + (m + water[idx]!) / 2);
        const eta = o.eta0 * Math.exp(o.c5 * tk + o.c6 * rho);
        const crOverburden = pressure / eta;
        const shrink = Math.exp(-(crDestructive + crOverburden) * dt);
        density[idx] = Math.min(EVOLUTION_MODEL.maxDensity, rho / shrink);
      }

      // Grain aging. Wet non-weak-layer snow becomes melt forms.
      const g = grain[idx]! as Grain;
      if (wet && !WEAK_LAYER_GRAINS.has(g) && g !== Grain.IF) {
        grain[idx] = Grain.MF;
      } else if (g === Grain.PP && age >= EVOLUTION_MODEL.ppToDfHours) {
        grain[idx] = Grain.DF;
      } else if (g === Grain.DF && age >= EVOLUTION_MODEL.dfToRgHours) {
        grain[idx] = Grain.RG;
      }

      above += m + water[idx]!;
      depthAbove += mass[idx]! / density[idx]!;
    }
  }
  return runoff;
}

/**
 * Refreeze up to `capacity` kg/m² of liquid water in the top `refreezeDepth` of a column, from
 * the surface down. The water becomes ice in place (thickness unchanged, density up) and the
 * layer becomes a melt-freeze crust — or an ice crust once it is dense enough. Crusts are strong
 * but are sliding surfaces, and facets like to grow next to them.
 */
function refreeze(cols: SnowColumns, cell: number, capacity: number): void {
  const base = cell * cols.maxLayers;
  let depth = 0;
  let left = capacity;
  const { refreezeDepth, minSplitThickness } = EVOLUTION_MODEL;
  for (let k = cols.count[cell]! - 1; k >= 0 && left > 0; k--) {
    if (depth >= refreezeDepth) break;
    // A wet layer reaching below the refreeze depth: only its upper part freezes into a crust.
    if (cols.water[base + k]! > 0) {
      const within = refreezeDepth - depth;
      const h0 = cols.mass[base + k]! / cols.density[base + k]!;
      if (h0 - within > minSplitThickness && within > minSplitThickness) {
        // The upper part becomes layer k + 1; the lower part (k) is below the refreeze depth.
        if (splitLayer(cols, cell, k, within)) k++;
      }
    }
    const idx = base + k;
    const h = cols.mass[idx]! / cols.density[idx]!;
    depth += h;
    const w = cols.water[idx]!;
    if (w <= 0) continue;
    const frozen = Math.min(w, left);
    left -= frozen;
    cols.water[idx] = w - frozen;
    cols.mass[idx] = cols.mass[idx]! + frozen;
    cols.density[idx] = Math.min(900, cols.mass[idx]! / h);
    if (cols.water[idx]! <= 1e-6) {
      cols.water[idx] = 0;
      cols.grain[idx] = cols.density[idx]! >= EVOLUTION_MODEL.iceDensity ? Grain.IF : Grain.MF;
    }
  }
}

/**
 * Add liquid water (rain) to the top of a column and let it percolate: each layer holds up to
 * `holdingCapacity` of its volume, the excess drains to the layer below, and whatever leaves the
 * bottom is runoff. Returns the runoff (kg/m²).
 */
export function addLiquidWater(cols: SnowColumns, cell: number, amount: number): number {
  const n = cols.count[cell]!;
  if (n === 0) return amount;
  const top = cell * cols.maxLayers + n - 1;
  cols.water[top] = cols.water[top]! + amount;
  return percolate(cols, cell);
}

/** Drain water exceeding each layer's holding capacity downwards; returns bottom runoff. */
function percolate(cols: SnowColumns, cell: number): number {
  const base = cell * cols.maxLayers;
  let carry = 0;
  for (let k = cols.count[cell]! - 1; k >= 0; k--) {
    const idx = base + k;
    const h = cols.mass[idx]! / cols.density[idx]!;
    const capacity = EVOLUTION_MODEL.holdingCapacity * h * WATER_DENSITY;
    const w = cols.water[idx]! + carry;
    if (w > capacity) {
      cols.water[idx] = capacity;
      carry = w - capacity;
    } else {
      cols.water[idx] = w;
      carry = 0;
    }
  }
  return carry;
}
